import qs from 'qs';
import { useMutation, useQuery } from 'react-query';
import { network } from 'services/network';

const getUsersList = () => {
    return network
        .get('admin/bo/getUsersList')
        .then((response) => response.data)
        .catch((error) => {
            throw error;
        });
};

const deleteUser = (params) => {
    const querystring = qs.stringify(
        { userName: params.email },
        { addQueryPrefix: true, skipNulls: true, encode: true },
    );
    let url = `admin/bo/delete${querystring}`;
    return network
        .post(url)
        .then((response) => response.data)
        .catch((error) => {
            throw error;
        });
};

const createUser = (params) => {
    return network
        .post('admin/bo/createUser', params)
        .then((response) => response.data)
        .catch((error) => {
            throw error;
        });
};
const updateUser = (params) => {
    return network
        .post('admin/bo/updateUser', params)
        .then((response) => response.data)
        .catch((error) => {
            throw error;
        });
};

const updateUserImage = (params) => {
    const querystring = qs.stringify({ userName: params.userName }, { addQueryPrefix: true, skipNulls: true });
    const url = `'api/bo/uploadUserImageByUserName${querystring}`;
    const formData = new FormData();
    if (params.file !== null) {
        if (typeof params.file.size !== 'undefined') {
            formData.append('file', params.file, params.file.name);
        }
    }
    return network
        .post(url, formData, {
            transformRequest: [
                (data, headers) => {
                    if (headers) {
                        headers['Content-Type'] = 'multipart/form-data';
                    }
                    return data;
                },
            ],
        })
        .then((response) => {
            response.data;
        })
        .catch((error) => {
            throw error;
        });
};

const getUserImage = (params) => {
    const querystring = qs.stringify({ email: params.email }, { addQueryPrefix: true, skipNulls: true });
    const url = `api/bo/getUserImage${querystring}`;
    return network
        .post(url, null, {
            transformRequest: [
                (data, headers) => {
                    if (headers) {
                        headers.Accept = 'image/png';
                    }
                    return data;
                },
            ],
            responseType: 'blob',
        })
        .then((response) => {
            return response.data;
        })
        .catch((error) => {
            throw error;
        });
};

const getUserDetails = (params) => {
    const querystring = qs.stringify({ username: params.email }, { addQueryPrefix: true, skipNulls: true });
    const url = `${'api/bo/getUserDetails'}${querystring}`;
    return network
        .get(url)
        .then((response) => response.data)
        .catch((error) => {
            throw error;
        });
};

type IPrivilegesMap = {
    [K in keyof IPrivileges]: {
        [K2 in K]: IPrivilegesType;
    };
}[keyof IPrivileges];

export type TMfaType = 'off' | 'email' | 'google' | 'microsoft';

export interface IGetUserDetailResponse {
    badLoginAttempts: number;
    customerId: number;
    defaultProjectId: number;
    email: string;
    firstName: string;
    globalFilterStatus: {
        showFilterToggleButton: boolean;
        showActivityCodeFilter: boolean;
        isFilterExpanded: boolean;
    };
    homePageUrl: string;
    image: string;
    lastLogin: number;
    lastName: string;
    logo: string;
    phone: string;
    privilegeMap: IPrivilegesMap;
    roleList: IRole[];
    status: 'ACTIVE';
    title: string;
    userGroupList: string[];
    username: string;
    uuid: string;
    mfa: TMfaType;
    confetti: boolean;
}

export const useQueryGetUserDetail = (params: { email?: string }) => {
    return useQuery<IGetUserDetailResponse, QueryError>(['getUserDetail', params], () => getUserDetails(params), {
        enabled: Boolean(params.email),
    });
};

export const useQueryUsersList = () => {
    return useQuery('usersList', () => getUsersList());
};

interface DeleteUserParams {
    email: string;
}
export const useMutationDeleteUser = () => {
    return useMutation<unknown, QueryError, DeleteUserParams>('deleteUser', (params: DeleteUserParams) =>
        deleteUser(params),
    );
};
export const useMutationCreateUser = () => {
    return useMutation<unknown, QueryError, unknown>('createUser', (params) => createUser(params));
};
export const useMutationUpdateUser = () => {
    return useMutation<unknown, QueryError, unknown>('updateUser', (params) => updateUser(params));
};

export const useMutationUploadUserImage = () => {
    return useMutation<unknown, QueryError, unknown>('updateUserImage', (params) => updateUserImage(params));
};

export const useQueryGetUserImage = (params) => {
    return useQuery<string | null, QueryError>(['getUserImage', params], () => getUserImage(params), {
        enabled: Boolean(params.email) && !params.isUnassigned && Boolean(params.imageName !== ''),
        staleTime: 1000 * 60 * 5,
    });
};

const changePassword = (params) => {
    return network
        .post('admin/bo/changePassword', params)
        .then((response) => {
            return response.status;
        })
        .catch((error) => {
            throw error;
        });
};

export const useMutationChangePassword = () => {
    return useMutation<unknown, QueryError, any>('changePassword', (params) => changePassword(params));
};
